/**
 * Standalone page entry — hosted at widget.mirlo.com/{slug}
 *
 * Reads the slug from the URL path, performs the handshake,
 * captures attribution data, and renders the chat fullscreen.
 *
 * Unlike the widget (IIFE, shadow DOM, floating button), this
 * renders as a normal page — no shadow DOM, no floating button.
 */

import { render } from 'preact';
// Tailwind + widget tokens — the embedded flow injects this into a shadow DOM
// via mount.tsx, but the standalone page is a regular document with no shadow
// root, so it needs a plain top-level import. Without this every `class=""`
// (bubbles, header, composer, timestamps) renders unstyled.
import './styles.css';
import { handshake, HandshakeError } from './client/handshake';
import { fetchVisitorHistory } from './client/history';
import { toMediaAttachment } from './client/media';
import { obtainProofOfWork } from './client/pow';
import { readVisitorId, writeVisitorId } from './client/storage';
import { initLocale } from './lib/i18n';
import { captureAttribution } from './lib/attribution';
import { setAttribution } from './lib/attribution-store';
import { StandalonePage } from './widget/StandalonePage';
import type { ChatMessage } from './widget/useChatState';
import type { HandshakeResponse } from './client/api-types';

const DEFAULT_API_BASE_URL =
  import.meta.env.VITE_DEFAULT_API_BASE_URL ?? 'https://messages.mirlo.com';

function getSlugFromPath(): string | null {
  // Production/CloudFront URLs:
  //   w.mirlo.com/{slug}
  //   w.mirlo.com/chat/{slug}
  //
  // Dev URLs (see vite.config.ts standaloneRouter middleware):
  //   http://localhost:5174/{slug}          ← same shape as prod
  //   http://localhost:5174/standalone.html?slug=... ← handy when the widget
  //     playground is already mounted at / and you want to open a specific
  //     standalone slug without leaving the same origin.
  //
  // The `?slug=` fallback is honored on any path so opening the raw entry
  // (/standalone.html) still works without landing on "Page not found".
  const parts = window.location.pathname.split('/').filter(Boolean);
  const first = parts[0];

  if (first === 'chat' && parts[1]) return parts[1];

  // Skip the dev entry filename itself — it's not a slug, and prod URLs will
  // never look like this because the CloudFront Function serves the file
  // silently from a rewritten path.
  if (first && first !== 'standalone.html') return first;

  return new URLSearchParams(window.location.search).get('slug');
}

function showError(message: string) {
  document.body.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#7A8791;text-align:center;padding:24px;">
      <div>
        <p style="font-size:18px;margin:0 0 8px">${message}</p>
        <p style="font-size:13px;margin:0;opacity:.6">Powered by mirlo</p>
      </div>
    </div>
  `;
}

async function boot(): Promise<void> {
  const slug = getSlugFromPath();

  if (!slug) {
    showError('Page not found');
    return;
  }

  // Capture attribution from URL params before anything else
  const attribution = captureAttribution();
  setAttribution(attribution);

  const existingVisitorId = readVisitorId();
  let response: HandshakeResponse;

  try {
    try {
      response = await handshake(
        DEFAULT_API_BASE_URL,
        slug, // The backend resolves by slug OR widgetId
        existingVisitorId ?? undefined,
        undefined,
        attribution as unknown as Record<string, unknown>,
      );
    } catch (firstErr) {
      if (
        firstErr instanceof HandshakeError &&
        firstErr.code === 'pow_required'
      ) {
        const pow = await obtainProofOfWork(DEFAULT_API_BASE_URL);
        response = await handshake(
          DEFAULT_API_BASE_URL,
          slug,
          existingVisitorId ?? undefined,
          pow,
          attribution as unknown as Record<string, unknown>,
        );
      } else {
        throw firstErr;
      }
    }
  } catch (err) {
    if (err instanceof HandshakeError) {
      if (err.code === 'widget_not_found') {
        showError('This page does not exist');
      } else if (err.code === 'widget_disabled') {
        showError('This chat is no longer available');
      } else {
        showError('Something went wrong. Please try again.');
      }
      console.error(`[Mirlo Standalone] Handshake failed: ${err.code}`);
    } else {
      showError('Connection error. Please try again.');
      console.error('[Mirlo Standalone] Unexpected error:', err);
    }
    return;
  }

  writeVisitorId(response.visitorId);
  initLocale(response.config.branding.locale);

  const config = response.config;
  const { branding } = config;

  // Update browser tab: title, favicon, theme-color
  document.title = `Chat con ${branding.name}`;

  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) themeColor.setAttribute('content', branding.color);

  // Favicon: use logo if available, otherwise generate from initial + brand color
  const favicon = document.getElementById('favicon') as HTMLLinkElement | null;
  if (favicon) {
    if (branding.logo_url) {
      favicon.type = 'image/png';
      favicon.href = branding.logo_url;
    } else {
      const initial = branding.name.charAt(0).toUpperCase();
      const color = branding.color.replace('#', '%23');
      favicon.href = `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect fill='${color}' rx='20' width='100' height='100'/><text x='50' y='72' text-anchor='middle' fill='white' font-size='55' font-family='system-ui' font-weight='bold'>${initial}</text></svg>`;
    }
  }

  const container = document.getElementById('app');
  if (!container) {
    showError('Mount point not found');
    return;
  }

  // Rehydrate the transcript before mounting so the panel opens straight
  // into the previous session (same-visitor inside 24h). Silent-fail: if
  // this errors the page still boots empty, which matches the pre-history
  // behavior and is preferable to blocking the mount.
  const initialMessages = await loadHistoryIfAny(
    DEFAULT_API_BASE_URL,
    slug,
    response.visitorId,
    response.sessionToken,
  );

  render(
    <StandalonePage
      branding={config.branding}
      preChatForm={config.preChatForm ?? null}
      captureMode={config.captureMode}
      quickReplies={config.quickReplies}
      verified={config.branding.verified}
      headerStyle={config.branding.header_style}
      bubbleStyle={config.branding.bubble_style}
      defaultTheme={config.branding.default_theme}
      visitorId={response.visitorId}
      sessionToken={response.sessionToken}
      apiBaseUrl={DEFAULT_API_BASE_URL}
      initialMessages={initialMessages}
      availability={config.availability}
      widgetId={slug}
      attachments={config.attachments}
    />,
    container,
  );
}

async function loadHistoryIfAny(
  apiBaseUrl: string,
  widgetId: string,
  visitorId: string,
  sessionToken: string,
): Promise<ChatMessage[]> {
  try {
    const res = await fetchVisitorHistory(
      apiBaseUrl,
      widgetId,
      visitorId,
      sessionToken,
    );
    return res.messages.map((m) => {
      const media = toMediaAttachment(apiBaseUrl, m);
      return {
        id: m.id,
        role: m.direction === 'incoming' ? 'user' : 'agent',
        text: m.text,
        timestamp: Date.parse(m.timestamp) || undefined,
        ...(media ? { media } : {}),
      } satisfies ChatMessage;
    });
  } catch (err) {
    console.warn(
      '[Mirlo Standalone] Could not fetch conversation history:',
      err,
    );
    return [];
  }
}

boot();
